import React from "react";
import { ColumnDef } from "@tanstack/react-table";
import { ArrowDownLeft, ArrowUpRight } from "lucide-react";

import DataTableColumnHeader from "../../../components/data-table/DataTableColumnHeader";
import StatusBadge from "../../../components/StatusBadge";
import { cn } from "../../../utils/cn";
import { formatRelativeDateTime } from "../../../utils/formatters";
import type { ActivityRow } from "./normalizeActivity";

export const activityColumns: ColumnDef<ActivityRow, any>[] = [
  {
    id: "date",
    accessorFn: (row) => row.date,
    meta: { label: "Date" },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Date" />,
    cell: ({ row }) => (
      <span className="text-[13px] font-medium text-foreground">
        {formatRelativeDateTime(row.original.date)}
      </span>
    ),
  },
  {
    accessorKey: "type",
    meta: { label: "Type" },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Type" />,
    cell: ({ row }) => {
      const isPayout = row.original.type === "Payout";
      return (
        <span className="inline-flex items-center gap-2">
          <span
            className={cn(
              "flex size-7 items-center justify-center rounded-full",
              isPayout
                ? "bg-destructive/10 text-destructive"
                : "bg-success/10 text-success"
            )}
          >
            {isPayout ? (
              <ArrowUpRight className="size-4" />
            ) : (
              <ArrowDownLeft className="size-4" />
            )}
          </span>
          <span className="text-[13px] font-semibold text-foreground">
            {row.original.type}
          </span>
        </span>
      );
    },
  },
  {
    accessorKey: "description",
    meta: { label: "Details" },
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Details" />
    ),
    cell: ({ row }) => (
      <span className="block max-w-[220px] truncate text-[13px] font-medium text-muted-foreground">
        {row.original.description}
      </span>
    ),
  },
  {
    accessorKey: "amountLabel",
    meta: { label: "Amount" },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Amount" />
    ),
    cell: ({ row }) => (
      <div>
        <p className="text-[14px] font-semibold text-foreground">
          {row.original.amountLabel}
        </p>
        {row.original.usdLabel && (
          <p className="text-[12px] font-medium text-muted-foreground">
            {row.original.usdLabel}
          </p>
        )}
      </div>
    ),
  },
  {
    accessorKey: "nairaLabel",
    meta: { label: "Naira value" },
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Naira value" />
    ),
    cell: ({ row }) => (
      <span className="text-[14px] font-semibold text-foreground">
        {row.original.nairaLabel}
      </span>
    ),
  },
  {
    accessorKey: "status",
    meta: { label: "Status" },
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => <StatusBadge status={row.original.status} />,
  },
];
